import type { CacheGroup } from '../cache'
import p from 'pathe'
import { StorageDownloadCache, StorageGetCache, StorageListCache } from '../cache'

function fixName(name: string): string {
  // Replace [BIN-<timestamp>] with ''
  let newName = name.replace(/\[BIN-\d+\]/, '')

  // escape +, &, #, <, >
  newName = newName.replace(/[+&#<>]/g, (char) => {
    return `_${char.charCodeAt(0)}_`
  })

  return newName
}

export class PathGuide {
  _path: string[] = []

  static toString(path: string | string[]): string {
    return new PathGuide(path).toString()
  }

  static toArray(path: string | string[]): string[] {
    return new PathGuide(path).toArray()
  }

  static from(path: string | string[]): PathGuide {
    return new PathGuide(path)
  }

  static #arrayifyPath(value: string | string[], keepEmpty = false): string[] {
    return Array.isArray(value)
      ? value
      : value.split('/').filter(x => keepEmpty || !!x)
  }

  constructor(path: string | string[]) {
    this.setPath(path)
  }

  cd(path: string): PathGuide {
    const newPath = p.resolve(this.toString(), path)

    return new PathGuide(newPath)
  }

  storage(): PathGuide | null {
    if (this._path.length) {
      return new PathGuide(this._path[0])
    }
    return null
  }

  async ls(): Promise<PathGuide[]> {
    const nodes = this.useList()

    const val = await nodes.tryValueAsync

    nodes.release()
    if (!val)
      return []
    return val.map(node => this.cd(node.name))
  }

  allAbove(): PathGuide[] {
    const paths: PathGuide[] = []

    let path = this as PathGuide
    do {
      paths.push(path)
      path = path.cd('..')
    } while (!path.root)

    return paths
  }

  distanceTo(other: PathGuide): number {
    const thisParts = this.toArray()
    const otherParts = other.toArray()

    let commonLength = 0
    const maxCommonLength = Math.min(thisParts.length, otherParts.length)
    while (commonLength < maxCommonLength && thisParts[commonLength] === otherParts[commonLength]) {
      commonLength++
    }

    return (thisParts.length - commonLength) + (otherParts.length - commonLength)
  }

  /**
   * Checks if the other PathGuide lies within the current PathGuide's directory (no  subdirectories) or inside of parents (also no subdirectories).
   * other can be a file or directory.
   */
  liesInParentDir(other: PathGuide): boolean {
    if (other.root)
      return false

    const otherParts = other.toArray()
    const thisParts = this.toArray()

    // For each ancestor directory (including this directory and root),
    // check if `other` lies directly inside that directory (exactly one
    // path segment deeper than the directory). This excludes items that
    // are deeper (subdirectories of this) or located in unrelated paths.
    for (let dirLength = thisParts.length; dirLength >= 0; dirLength--) {
      if (otherParts.length !== dirLength + 1)
        continue

      let matched = true
      for (let i = 0; i < dirLength; i++) {
        if (otherParts[i] !== thisParts[i]) {
          matched = false
          break
        }
      }

      if (matched)
        return true
    }

    return false
  }

  relative(other: PathGuide): string {
    return p.relative(this.toString(), other.toString())
  }

  get safeName(): string {
    return fixName(p.basename(this.toString()))
  }

  get name(): string {
    return p.basename(this.toString())
  }

  get nameReadable(): string {
    return this.name.split('.')[0]
  }

  get extension(): string {
    return this.name.split('.').slice(1).join('.')
  }

  get root(): boolean {
    return this._path.length === 0
  }

  get isStorage(): boolean {
    return this._path.length === 1
  }

  async exists(): Promise<boolean> {
    const node = await StorageGetCache.use(this.toString())
    const value = await node.tryValueAsync
    node.release()
    return !!value
  }

  equals(other: PathGuide): boolean {
    return this.toString() === other.toString()
  }

  useGet(
    cacheGroup?: CacheGroup,
    skipRetrieve?: boolean,
    lazyUpdate?: boolean,
  ): ReturnType<typeof StorageGetCache.use> {
    return StorageGetCache.use(this.toString(), cacheGroup, skipRetrieve, lazyUpdate)
  }

  useList(cacheGroup?: CacheGroup): ReturnType<typeof StorageListCache.use> {
    return StorageListCache.use(this.toString(), cacheGroup)
  }

  useDownload(
    cacheGroup?: CacheGroup,
  ): ReturnType<typeof StorageDownloadCache.use> {
    return StorageDownloadCache.use(this.toString(), cacheGroup)
  }

  setPath(path: string | string[]) {
    this._path = PathGuide.#arrayifyPath(path)
  }

  toString(): string {
    return `/${this._path.join('/')}`
  }

  toArray(): string[] {
    return this._path.filter(Boolean)
  }
}
